import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Button, Typography, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Alert, Chip, MenuItem, IconButton, Tooltip, Avatar,
} from '@mui/material';
import {
  Edit as EditIcon, Add as AddIcon, PersonAdd as PersonAddIcon,
  TrendingUp, People, AccountBalance, ConfirmationNumber, ArrowUpward, CheckCircle,
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  BarChart, Bar, AreaChart, Area
} from 'recharts';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function AdminSubscriptions() {
  const [tabValue, setTabValue] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [planDialog, setPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  
  const [userDialog, setUserDialog] = useState(false);
  const [userSubscriptionDialog, setUserSubscriptionDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [couponDialog, setCouponDialog] = useState(false);

  // Form states
  const [planFormData, setPlanFormData] = useState({
    name: '', slug: '', description: '', price_monthly: 0, price_yearly: 0, trial_days: 0, is_active: 1
  });

  const [userFormData, setUserFormData] = useState({
    username: '', email: '', password: '', role: 'user', plan_id: 1, start_date: '', end_date: '', status: 'ACTIVE'
  });

  const [userSubFormData, setUserSubFormData] = useState({
    planId: 1, startDate: '', endDate: '', status: 'ACTIVE'
  });

  const [couponFormData, setCouponFormData] = useState({
    code: '', discount_type: 'PERCENTAGE', discount_value: 0, max_uses: '', valid_until: ''
  });

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, revenueRes, plansRes, usersRes, couponsRes] = await Promise.all([
        api.get('/admin/subscriptions/analytics/overview'),
        api.get('/admin/subscriptions/analytics/revenue?days=30'),
        api.get('/admin/subscriptions/plans'),
        api.get('/admin/subscriptions/users'),
        api.get('/admin/subscriptions/coupons')
      ]);

      setAnalytics(analyticsRes.data?.overview || null);
      setRevenue(Array.isArray(revenueRes.data?.revenue) ? revenueRes.data.revenue : []);
      setPlans(Array.isArray(plansRes.data?.plans) ? plansRes.data.plans : []);
      setUsers(Array.isArray(usersRes.data?.users) ? usersRes.data.users : []);
      setCoupons(Array.isArray(couponsRes.data?.coupons) ? couponsRes.data.coupons : []);
    } catch (err) {
      console.error('Error loading admin data:', err);
      toast.error('Failed to load admin subscription data');
    } finally {
      setLoading(false);
    }
  };

  // ─── Plan Handlers ──────────────────────────────────────────────────────────
  const handleOpenCreatePlan = () => {
    setEditingPlan(null);
    setPlanFormData({ name: '', slug: '', description: '', price_monthly: 0, price_yearly: 0, trial_days: 0, is_active: 1 });
    setPlanDialog(true);
  };

  const handleOpenEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanFormData({
      name: plan.name || '',
      slug: plan.slug || '',
      description: plan.description || '',
      price_monthly: plan.price_monthly || 0,
      price_yearly: plan.price_yearly || 0,
      trial_days: plan.trial_days || 0,
      is_active: plan.is_active ? 1 : 0
    });
    setPlanDialog(true);
  };

  const handleSavePlan = async () => {
    try {
      if (editingPlan) {
        await api.put(`/admin/subscriptions/plans/${editingPlan.id}`, planFormData);
        toast.success('Plan updated successfully!');
      } else {
        await api.post('/admin/subscriptions/plans', planFormData);
        toast.success('Plan created successfully!');
      }
      setPlanDialog(false);
      loadAdminData();
    } catch (err) {
      console.error('Error saving plan:', err);
      toast.error(err.response?.data?.message || 'Error saving plan');
    }
  };

  // ─── User Handlers ──────────────────────────────────────────────────────────
  const handleOpenCreateUser = () => {
    const today = new Date().toISOString().split('T')[0];
    setUserFormData({
      username: '', email: '', password: '', role: 'user', plan_id: 1, start_date: today, end_date: '', status: 'ACTIVE'
    });
    setUserDialog(true);
  };

  const handleCreateUser = async () => {
    try {
      if (!userFormData.username || !userFormData.email || !userFormData.password) {
        toast.error('Username, Email and Password are required');
        return;
      }
      await api.post('/admin/subscriptions/users', userFormData);
      toast.success('User created and plan assigned successfully!');
      setUserDialog(false);
      loadAdminData();
    } catch (err) {
      console.error('Error creating user:', err);
      toast.error(err.response?.data?.message || 'Error creating user');
    }
  };

  const handleOpenEditUserSub = (user) => {
    setSelectedUser(user);
    const matchedPlan = plans.find(p => p.slug === user.plan_slug || p.name === user.plan_name);
    const planId = matchedPlan ? matchedPlan.id : (user.plan_id || 1);
    
    setUserSubFormData({
      planId: planId,
      startDate: user.start_date ? user.start_date.split('T')[0] : new Date().toISOString().split('T')[0],
      endDate: user.end_date ? user.end_date.split('T')[0] : '',
      status: user.status || 'ACTIVE'
    });
    setUserSubscriptionDialog(true);
  };

  const handleSaveUserSub = async () => {
    try {
      if (!selectedUser) return;
      await api.post(`/admin/subscriptions/users/${selectedUser.id}/subscription`, userSubFormData);
      toast.success(`Plan updated for user ${selectedUser.username || selectedUser.email}`);
      setUserSubscriptionDialog(false);
      loadAdminData();
    } catch (err) {
      console.error('Error updating user plan:', err);
      toast.error(err.response?.data?.message || 'Error updating user plan');
    }
  };

  // ─── Coupon Handlers ────────────────────────────────────────────────────────
  const handleCreateCoupon = async () => {
    try {
      await api.post('/admin/subscriptions/coupons', couponFormData);
      toast.success('Coupon created successfully!');
      setCouponDialog(false);
      setCouponFormData({ code: '', discount_type: 'PERCENTAGE', discount_value: 0, max_uses: '', valid_until: '' });
      loadAdminData();
    } catch (err) {
      console.error('Error creating coupon:', err);
      toast.error(err.response?.data?.message || 'Error creating coupon');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header Banner */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3.5, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', mb: 0.5 }}>
            SaaS Subscription Control Panel
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Manage platform users, subscription plan tiers, recurring revenues, and promo coupons.
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="medium"
          startIcon={<PersonAddIcon />}
          onClick={handleOpenCreateUser}
          sx={{
            py: 1.2,
            px: 2.5,
            fontSize: '0.875rem',
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)'
          }}
        >
          Create New User / Owner
        </Button>
      </Box>

      {/* KPI Cards Grid */}
      {analytics && (
        <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#ffffff', p: 0.5 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em' }}>
                    ACTIVE SUBSCRIBERS
                  </Typography>
                  <Avatar sx={{ bgcolor: '#eff6ff', color: '#2563eb', width: 40, height: 40, borderRadius: 2.5 }}>
                    <People fontSize="small" />
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
                  {analytics.active_subscribers}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip label="Platform Accounts" size="small" sx={{ bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.7rem', fontWeight: 600 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#ffffff', p: 0.5 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em' }}>
                    MONTHLY REVENUE (MRR)
                  </Typography>
                  <Avatar sx={{ bgcolor: '#ecfdf5', color: '#10b981', width: 40, height: 40, borderRadius: 2.5 }}>
                    <AccountBalance fontSize="small" />
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
                  ₹{analytics.mrr ? analytics.mrr.toLocaleString('en-IN') : 0}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                    <ArrowUpward sx={{ fontSize: 14 }} /> Active Run Rate
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#ffffff', p: 0.5 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em' }}>
                    ANNUAL RUN RATE (ARR)
                  </Typography>
                  <Avatar sx={{ bgcolor: '#f5f3ff', color: '#7c3aed', width: 40, height: 40, borderRadius: 2.5 }}>
                    <TrendingUp fontSize="small" />
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
                  ₹{analytics.arr ? analytics.arr.toLocaleString('en-IN') : 0}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                    Projected Annual Base
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#ffffff', p: 0.5 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em' }}>
                    THIS MONTH REVENUE
                  </Typography>
                  <Avatar sx={{ bgcolor: '#fff7ed', color: '#f97316', width: 40, height: 40, borderRadius: 2.5 }}>
                    <ConfirmationNumber fontSize="small" />
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
                  ₹{analytics.monthly_revenue ? analytics.monthly_revenue.toLocaleString('en-IN') : 0}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                    Current Billing Cycle
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Analytics Charts */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5, color: '#0f172a' }}>
                Revenue Growth Trend (30 Days)
              </Typography>
              {revenue.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={revenue}>
                    <defs>
                      <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} tickLine={false} />
                    <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} />
                    <RechartsTooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                    <Area type="monotone" dataKey="amount" stroke="#2563eb" strokeWidth={3} fillOpacity={1} fill="url(#revenueGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 3 }}>No revenue transactions recorded in the last 30 days</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5, color: '#0f172a' }}>
                Subscriber Distribution by Plan
              </Typography>
              {analytics?.subscriber_breakdown?.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={analytics.subscriber_breakdown}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} />
                    <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} />
                    <RechartsTooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                    <Bar dataKey="count" fill="#10b981" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 3 }}>No subscriber distribution data available</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs Section */}
      <Card sx={{ mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: '#e2e8f0', px: 3, pt: 2 }}>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                minHeight: 48,
                px: 2.5,
              }
            }}
          >
            <Tab label="User Directory & Plan Assignment" />
            <Tab label="Subscription Plans Matrix" />
            <Tab label="Coupons & Promotional Codes" />
          </Tabs>
        </Box>

        {/* Tab 0: User Directory */}
        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                Registered Accounts ({users.length})
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={handleOpenCreateUser}
              >
                Add User
              </Button>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>USER ACCOUNT</TableCell>
                    <TableCell>EMAIL</TableCell>
                    <TableCell>ROLE</TableCell>
                    <TableCell>CURRENT PLAN</TableCell>
                    <TableCell>STATUS</TableCell>
                    <TableCell>EXPIRY</TableCell>
                    <TableCell align="right">ACTION</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(users) && users.map((user) => {
                    const planName = user.plan_name || 'Free Trial';
                    const isPremium = planName.toLowerCase().includes('premium');
                    const isPro = planName.toLowerCase().includes('pro');

                    return (
                      <TableRow key={user.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>#{user.id}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#2563eb', fontSize: '0.8rem', fontWeight: 700 }}>
                              {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
                            </Avatar>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{user.username}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: '#475569' }}>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.role ? user.role.toUpperCase() : 'USER'}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              bgcolor: user.role === 'admin' ? '#fee2e2' : '#f1f5f9',
                              color: user.role === 'admin' ? '#991b1b' : '#475569',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={planName}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              bgcolor: isPremium ? '#fef3c7' : isPro ? '#e0e7ff' : '#f1f5f9',
                              color: isPremium ? '#b45309' : isPro ? '#3730a3' : '#475569',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.status || 'ACTIVE'}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              bgcolor: user.status === 'ACTIVE' ? '#dcfce7' : '#fee2e2',
                              color: user.status === 'ACTIVE' ? '#15803d' : '#991b1b',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                          {user.end_date ? new Date(user.end_date).toLocaleDateString() : 'Lifetime'}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenEditUserSub(user)}
                            sx={{ borderRadius: 2 }}
                          >
                            Manage Plan
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Tab 1: Plans Matrix */}
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                Subscription Tier Plans
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenCreatePlan}
              >
                Create Custom Tier
              </Button>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>PLAN NAME</TableCell>
                    <TableCell>SLUG</TableCell>
                    <TableCell>MONTHLY PRICE</TableCell>
                    <TableCell>YEARLY PRICE</TableCell>
                    <TableCell>TRIAL DAYS</TableCell>
                    <TableCell>STATUS</TableCell>
                    <TableCell align="right">ACTION</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(plans) && plans.map((plan) => (
                    <TableRow key={plan.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>#{plan.id}</TableCell>
                      <TableCell><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{plan.name}</Typography></TableCell>
                      <TableCell><Chip label={plan.slug} size="small" sx={{ fontFamily: 'monospace', fontWeight: 600 }} /></TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                        ₹{parseFloat(plan.price_monthly || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                        ₹{parseFloat(plan.price_yearly || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell>{plan.trial_days || 0} days</TableCell>
                      <TableCell>
                        <Chip
                          label={plan.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor: plan.is_active ? '#dcfce7' : '#f1f5f9',
                            color: plan.is_active ? '#15803d' : '#64748b'
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenEditPlan(plan)}
                          sx={{ borderRadius: 2 }}
                        >
                          Edit Plan
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Tab 2: Coupons */}
        {tabValue === 2 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                Active Promotional Coupons
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCouponDialog(true)}
              >
                New Coupon Code
              </Button>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>COUPON CODE</TableCell>
                    <TableCell>DISCOUNT TYPE</TableCell>
                    <TableCell>DISCOUNT VALUE</TableCell>
                    <TableCell>MAX USES</TableCell>
                    <TableCell>USAGE COUNT</TableCell>
                    <TableCell>VALID UNTIL</TableCell>
                    <TableCell>STATUS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(coupons) && coupons.map((coupon) => (
                    <TableRow key={coupon.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>
                        <Chip label={coupon.code} sx={{ fontWeight: 800, letterSpacing: '0.05em', bgcolor: '#eff6ff', color: '#2563eb' }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{coupon.discount_type}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#10b981' }}>
                        {coupon.discount_type === 'PERCENTAGE' ? `${coupon.discount_value}% OFF` : `₹${coupon.discount_value} OFF`}
                      </TableCell>
                      <TableCell>{coupon.max_uses || 'Unlimited'}</TableCell>
                      <TableCell>{coupon.usage_count || 0}</TableCell>
                      <TableCell>{coupon.valid_until ? new Date(coupon.valid_until).toLocaleDateString() : 'Lifetime'}</TableCell>
                      <TableCell>
                        <Chip
                          label={coupon.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor: coupon.is_active ? '#dcfce7' : '#fee2e2',
                            color: coupon.is_active ? '#15803d' : '#991b1b'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Card>

      {/* ─── Dialogs ───────────────────────────────────────────────────────────── */}
      {/* Create User Dialog */}
      <Dialog open={userDialog} onClose={() => setUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.2rem', pb: 1 }}>
          Create Account & Assign Plan
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth label="Username" required
              value={userFormData.username}
              onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
            />
            <TextField
              fullWidth label="Email Address" type="email" required
              value={userFormData.email}
              onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
            />
            <TextField
              fullWidth label="Password" type="password" required
              value={userFormData.password}
              onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
            />
            <TextField
              select fullWidth label="Assign Subscription Plan"
              value={userFormData.plan_id}
              onChange={(e) => setUserFormData({ ...userFormData, plan_id: e.target.value })}
            >
              {plans.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} (ID: {p.id}) — {p.id === 3 ? 'Full Live Trading & Auto Execution' : 'Analysis & Paper Trading Only'}
                </MenuItem>
              ))}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth type="date" label="Start Date" InputLabelProps={{ shrink: true }}
                  value={userFormData.start_date}
                  onChange={(e) => setUserFormData({ ...userFormData, start_date: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth type="date" label="Expiry Date (Optional)" InputLabelProps={{ shrink: true }}
                  value={userFormData.end_date}
                  onChange={(e) => setUserFormData({ ...userFormData, end_date: e.target.value })}
                />
              </Grid>
            </Grid>
            <TextField
              select fullWidth label="System Permission Role"
              value={userFormData.role || 'user'}
              onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
            >
              <MenuItem value="user">Customer / Tenant Owner (Standard Permissions)</MenuItem>
              <MenuItem value="admin">Superadmin (Full System Access)</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setUserDialog(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">Create & Assign Plan</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Subscription Dialog */}
      <Dialog open={userSubscriptionDialog} onClose={() => setUserSubscriptionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.2rem', pb: 1 }}>
          Assign / Change Plan — {selectedUser?.username || selectedUser?.email}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Changing plan will immediately update user trading permissions. Portfolio, trade journal, reports and paper trading history are 100% preserved.
            </Alert>
            <TextField
              select fullWidth label="Select New Plan"
              value={userSubFormData.planId}
              onChange={(e) => setUserSubFormData({ ...userSubFormData, planId: e.target.value })}
            >
              {plans.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} — {p.id === 3 ? 'Full Live Trading Allowed' : 'Analysis & Paper Only (Live Orders Restricted)'}
                </MenuItem>
              ))}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth type="date" label="Start Date" InputLabelProps={{ shrink: true }}
                  value={userSubFormData.startDate}
                  onChange={(e) => setUserSubFormData({ ...userSubFormData, startDate: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth type="date" label="Expiry Date" InputLabelProps={{ shrink: true }}
                  value={userSubFormData.endDate}
                  onChange={(e) => setUserSubFormData({ ...userSubFormData, endDate: e.target.value })}
                />
              </Grid>
            </Grid>
            <TextField
              select fullWidth label="Subscription Status"
              value={userSubFormData.status}
              onChange={(e) => setUserSubFormData({ ...userSubFormData, status: e.target.value })}
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="TRIAL">TRIAL</MenuItem>
              <MenuItem value="EXPIRED">EXPIRED</MenuItem>
              <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setUserSubscriptionDialog(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button onClick={handleSaveUserSub} variant="contained">Save Subscription</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog open={planDialog} onClose={() => setPlanDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.2rem', pb: 1 }}>
          {editingPlan ? `Edit Plan — ${editingPlan.name}` : 'Create Custom Tier'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth label="Plan Name" required
              value={planFormData.name}
              onChange={(e) => setPlanFormData({ ...planFormData, name: e.target.value })}
            />
            <TextField
              fullWidth label="Slug" required disabled={!!editingPlan}
              value={planFormData.slug}
              onChange={(e) => setPlanFormData({ ...planFormData, slug: e.target.value })}
            />
            <TextField
              fullWidth label="Description" multiline rows={2}
              value={planFormData.description}
              onChange={(e) => setPlanFormData({ ...planFormData, description: e.target.value })}
            />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth type="number" label="Monthly Price (₹)"
                  value={planFormData.price_monthly}
                  onChange={(e) => setPlanFormData({ ...planFormData, price_monthly: e.target.value })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth type="number" label="Yearly Price (₹)"
                  value={planFormData.price_yearly}
                  onChange={(e) => setPlanFormData({ ...planFormData, price_yearly: e.target.value })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth type="number" label="Trial Days"
                  value={planFormData.trial_days}
                  onChange={(e) => setPlanFormData({ ...planFormData, trial_days: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setPlanDialog(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button onClick={handleSavePlan} variant="contained">Save Plan</Button>
        </DialogActions>
      </Dialog>

      {/* Create Coupon Dialog */}
      <Dialog open={couponDialog} onClose={() => setCouponDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.2rem', pb: 1 }}>
          Create Promotional Coupon
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth label="Coupon Code" required
              value={couponFormData.code}
              onChange={(e) => setCouponFormData({ ...couponFormData, code: e.target.value })}
            />
            <TextField
              select fullWidth label="Discount Type"
              value={couponFormData.discount_type}
              onChange={(e) => setCouponFormData({ ...couponFormData, discount_type: e.target.value })}
            >
              <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
              <MenuItem value="FIXED">Fixed Amount (₹)</MenuItem>
            </TextField>
            <TextField
              fullWidth type="number" label="Discount Value" required
              value={couponFormData.discount_value}
              onChange={(e) => setCouponFormData({ ...couponFormData, discount_value: e.target.value })}
            />
            <TextField
              fullWidth type="number" label="Max Uses"
              value={couponFormData.max_uses}
              onChange={(e) => setCouponFormData({ ...couponFormData, max_uses: e.target.value })}
            />
            <TextField
              fullWidth type="date" label="Valid Until" InputLabelProps={{ shrink: true }}
              value={couponFormData.valid_until}
              onChange={(e) => setCouponFormData({ ...couponFormData, valid_until: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setCouponDialog(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button onClick={handleCreateCoupon} variant="contained">Create Coupon</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
